import React, { createContext, useContext, useState, useCallback } from "react";
import {
  sourceRefsListFromApiRule,
  zoneRefsListFromApiRule,
  sourceRefsNamesLabelFromApiRule,
  sourceRefsWithZonesFromApiRule,
} from "../utils/alertRuleSourceRefsUtils";

/* --- CONTEXT --- */

const AlertDataContext = createContext(undefined);

/* --- INITIAL STATE --- */

const initialSourceData = [

];

const initialCameras = [
  
];

const formatAlertTypeLabel = (alertType) => {
  if (!alertType) return "Object Detection";
  return String(alertType)
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
};

/** Prefer API `alert_type_name` (e.g. "ANPR Detection") for rule tables; else format `alert_type` slug. */
const alertTypeDisplayFromApiRule = (rule) => {
  if (!rule || typeof rule !== "object") {
    return formatAlertTypeLabel(null);
  }
  const name =
    rule.alert_type_name != null && String(rule.alert_type_name).trim() !== ""
      ? String(rule.alert_type_name).trim()
      : rule.alertTypeName != null && String(rule.alertTypeName).trim() !== ""
        ? String(rule.alertTypeName).trim()
        : null;
  return name || formatAlertTypeLabel(rule.alert_type);
};

/** Use-case UUID for GET /alerts/history `alert_type` (same as mainAlertServices / AddAlertRule). */
const resolveUseCaseIdFromApiRule = (rule) => {
  if (!rule || typeof rule !== "object") return "";
  if (rule.use_case_id != null && String(rule.use_case_id).trim() !== "") {
    return String(rule.use_case_id).trim();
  }
  if (rule.usecase_id != null && String(rule.usecase_id).trim() !== "") {
    return String(rule.usecase_id).trim();
  }
  const at = rule.alert_type;
  if (at != null && String(at).trim() !== "") {
    const s = String(at).trim();
    if (
      /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(
        s,
      )
    ) {
      return s;
    }
  }
  return "";
};

const sourceLabelFromApiRule = (rule) => {
  const st = String(rule.source_type || "").toLowerCase();
  const sn = rule.source_name || "";
  if (st === "iot_device" || st === "iot") return "IoT";
  if (st === "system") return "System";
  const zoneMatch = sn.match(/zone\s*0*(\d+)/i) || sn.match(/ROI\s*0*(\d+)/i);
  if (zoneMatch) {
    const n = parseInt(zoneMatch[1], 10);
    return `Zone ${String(n).padStart(2, "0")}`;
  }
  if (st === "camera" || st === "") return "AI";
  return formatAlertTypeLabel(rule.source_type) || "—";
};

const notifiedToFromRule = (rule) => {
  if (typeof rule.notify_to === "string" && rule.notify_to.trim())
    return rule.notify_to.trim();
  if (typeof rule.notified_to === "string" && rule.notified_to.trim())
    return rule.notified_to.trim();
  if (Array.isArray(rule.notify_recipients) && rule.notify_recipients.length)
    return rule.notify_recipients.join(", ");
  if (Array.isArray(rule.recipient_names) && rule.recipient_names.length)
    return rule.recipient_names.join(", ");
  return "";
};

function formatSeverityLabel(severity) {
  if (severity == null || severity === "") return "—";
  const s = String(severity).toLowerCase().trim();
  const map = {
    critical: "Critical",
    high: "High",
    medium: "Medium",
    low: "Low",
  };
  return map[s] || s.charAt(0).toUpperCase() + s.slice(1);
}

/** High-level source column: AI | IoT | System | Zone nn (from name). */
function deriveSourceCategory(sourceTypeRaw, sourceName) {
  const st = String(sourceTypeRaw || "").toLowerCase();
  const sn = String(sourceName || "");
  if (st.includes("iot")) return "IoT";
  if (st.includes("system")) return "System";
  const zm = sn.match(/\bzone\s*0*(\d+)/i);
  if (zm) return `Zone ${zm[1].padStart(2, "0")}`;
  if (st.includes("camera") || st.includes("roi")) return "AI";
  return "AI";
}

function extractNotifiedTo(rule) {
  const v =
    rule.notify_to ??
    rule.notified_to ??
    rule.notified_users ??
    rule.notify_users ??
    rule.user_names ??
    rule.recipients_display ??
    rule.notified_to_text;
  if (Array.isArray(v)) {
    const joined = v.filter(Boolean).join(", ");
    return joined || "—";
  }
  if (typeof v === "string" && v.trim()) return v.trim();
  return "—";
}

/* --- ALERT GENERATOR --- */

const generateAlertsFromRules = (rules) => {
  const alerts = [];
  const activeRules = rules.filter((rule) => rule.isActive);

  activeRules.forEach((rule) => {
    const alertCount = Math.floor(Math.random() * 2) + 2;

    for (let i = 0; i < alertCount; i++) {
      const alertDate = new Date();
      alertDate.setDate(alertDate.getDate() - Math.floor(Math.random() * 30));
      alertDate.setHours(Math.floor(Math.random() * 24));
      alertDate.setMinutes(Math.floor(Math.random() * 60));
      alertDate.setSeconds(Math.floor(Math.random() * 60));

      const cameraMatch = rule.sourceName.match(
        /Camera \d+|Local RTSP Stream|IoT Device \d+|System/,
      );
      const camera = cameraMatch ? cameraMatch[0] : "Unknown";

      const roiMatch = rule.sourceName.match(/ROI (\d+)/);
      const zone = roiMatch ? roiMatch[1] : "01";

      const objects = ["Person", "Vehicle", "Package", "Animal"];
      const object = objects[Math.floor(Math.random() * objects.length)];
      const count = Math.floor(Math.random() * 3) + 1;

      alerts.push({
        id: alerts.length + 1,
        message: `${count} ${object.toLowerCase()}${
          count > 1 ? "s" : ""
        } detected in zone '${zone}'`,
        camera,
        zone,
        object,
        timestamp: alertDate.toLocaleString(),
        videoTime: `00:0${Math.floor(Math.random() * 6)}:${String(
          Math.floor(Math.random() * 60),
        ).padStart(2, "0")}`,
        alertName: rule.name,
        alertType: rule.alertType,
        date: alertDate,
        ruleId: rule.id,
      });
    }
  });

  return alerts.sort((a, b) => b.date - a.date);
};

/* --- PROVIDER --- */

export function AlertDataProvider({ children }) {
  const [alertRules, setAlertRules] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [sourceData] = useState(initialSourceData);
  const [cameras] = useState(initialCameras);

  const addAlertRule = (rule) => {
    setAlertRules((prev) => [...prev, rule]);
    if (rule.isActive) generateAlertsForRule(rule);
  };

  const updateAlertRule = (id, updates) => {
    setAlertRules((prev) =>
      prev.map((rule) => (rule.id === id ? { ...rule, ...updates } : rule)),
    );
  };

  const deleteAlertRule = (id) => {
    setAlertRules((prev) => prev.filter((rule) => rule.id !== id));
    setAlerts((prev) => prev.filter((alert) => alert.ruleId !== id));
  };

  const toggleAlertRuleActive = (id) => {
    setAlertRules((prev) =>
      prev.map((rule) => {
        if (rule.id === id) {
          if (rule.isActive) {
            setAlerts((prevAlerts) =>
              prevAlerts.filter((a) => a.ruleId !== id),
            );
          } else {
            setTimeout(
              () => generateAlertsForRule({ ...rule, isActive: true }),
              0,
            );
          }
          return { ...rule, isActive: !rule.isActive };
        }
        return rule;
      }),
    );
  };

  const generateAlertsForRule = (rule) => {
    const newAlerts = generateAlertsFromRules([rule]);
    setAlerts((prev) =>
      [...newAlerts, ...prev].sort((a, b) => b.date - a.date),
    );
  };

  const clearAllAlerts = () => setAlerts([]);

  const refreshAlerts = () => setAlerts(generateAlertsFromRules(alertRules));

  /** Human-readable label from `source_ref` (object or string) or legacy name fields. */
  const sourceRefDisplayNameFromApi = (rule) => {
    if (!rule || typeof rule !== "object") return "";
    const sr = rule.source_ref;
    if (sr != null && typeof sr === "object") {
      const n =
        sr.name ??
        sr.label ??
        sr.display_name ??
        sr.title ??
        sr.zone_name;
      if (n != null && String(n).trim()) return String(n).trim();
    }
    if (typeof sr === "string" && String(sr).trim()) return String(sr).trim();
    for (const k of ["source_ref_name", "sourceRefName", "source_ref_label"]) {
      const v = rule[k];
      if (v != null && String(v).trim()) return String(v).trim();
    }
    return "";
  };

  const loadAlertRulesFromAPI = useCallback((apiRules) => {
    const transformedRules = apiRules.map((rule, index) => {
      const actionTypesMap = {
        sms: "SMS",
        telegram: "Telegram",
        email: "Email",
        http: "HTTP",
      };
      const actionTypes = (rule.action_types || []).map(
        (type) => actionTypesMap[String(type).toLowerCase()] || type,
      );

      const sourceRefsList = sourceRefsListFromApiRule(rule);
      const sourceRefsWithZones = sourceRefsWithZonesFromApiRule(rule);
      const zoneRefsList = zoneRefsListFromApiRule(rule);
      const sourceRefsNamesLabel = sourceRefsNamesLabelFromApiRule(rule);
      const singularSourceRefName = sourceRefDisplayNameFromApi(rule);
      const sourceRefName =
        sourceRefsNamesLabel || singularSourceRefName;
      const sourceNameResolved =
        rule.source_name || `Source ${rule.source_id || ""}`;
      const sourceTypeResolved =
        rule.source_type === "camera"
          ? "Camera + ROI"
          : rule.source_type || "Camera";

      const scheduleStr = (() => {
        const s = rule.schedule;
        if (!s || typeof s !== "object") return "Forever";
        const dayNameMap = {
          sun: "Sun",
          mon: "Mon",
          tue: "Tue",
          wed: "Wed",
          thu: "Thu",
          fri: "Fri",
          sat: "Sat",
          sunday: "Sun",
          monday: "Mon",
          tuesday: "Tue",
          wednesday: "Wed",
          thursday: "Thu",
          friday: "Fri",
          saturday: "Sat",
        };
        const oldDays = s.days || [];
        if (oldDays.length > 0 && oldDays.length < 7) {
          return oldDays
            .map((d) => dayNameMap[String(d).toLowerCase()] || d)
            .join(", ");
        }
        const dayKeys = [
          "monday",
          "tuesday",
          "wednesday",
          "thursday",
          "friday",
          "saturday",
          "sunday",
        ];
        const hasSlots = (day) => Array.isArray(s[day]) && s[day].length > 0;
        const daysWithSlots = dayKeys.filter((d) => hasSlots(d));
        if (daysWithSlots.length === 0 || daysWithSlots.length === 7) {
          return "Forever";
        }
        return daysWithSlots.map((d) => dayNameMap[d] || d).join(", ");
      })();

      const notifiedToStr =
        notifiedToFromRule(rule) || extractNotifiedTo(rule);

      const srIdRaw =
        rule.sr_id ??
        rule.srId ??
        rule.serial_id ??
        null;
      const srId =
        srIdRaw != null && String(srIdRaw).trim() !== ""
          ? String(srIdRaw).trim()
          : "";

      return {
        id: String(rule.id || `A${String(index + 1).padStart(3, "0")}`),
        apiId: rule.id,
        /** Backend serial / display id (`sr_id`). */
        srId,
        useCaseId: resolveUseCaseIdFromApiRule(rule),
        name: rule.alert_name || rule.name || "Unnamed Rule",
        isFav: rule.is_fav === true,
        isActive:
          rule.is_enabled !== undefined
            ? rule.is_enabled
            : rule.is_active !== undefined
              ? rule.is_active
              : true,
        alertType: alertTypeDisplayFromApiRule(rule),
        severityLabel: formatSeverityLabel(
          rule.rule_severity ?? rule.severity,
        ),
        schedule: scheduleStr,
        sourceName: sourceNameResolved,
        sourceCategory: deriveSourceCategory(
          rule.source_type,
          sourceNameResolved,
        ),
        sourceLabel: sourceLabelFromApiRule(rule),
        /** Sub-source column: `source_refs` names, else singular `source_ref`, else `source_name`. */
        subSourceLabel:
          sourceRefsNamesLabel ||
          singularSourceRefName ||
          sourceNameResolved,
        sourceRefName,
        /** For Sub-source popup: cameras/sources from API `source_refs`. */
        sourceRefsList,
        /** Accordion: each sub-source + its zone list (for Sub source & Zones dialog). */
        sourceRefsWithZones,
        /** For Sub-source popup: zones from `zone_refs` or nested under `source_refs`. */
        zoneRefsList,
        notifiedTo: notifiedToStr,
        sourceId:
          rule.source_ids &&
          Array.isArray(rule.source_ids) &&
          rule.source_ids.length > 0
            ? rule.source_ids.join(", ")
            : rule.source_id || null,
        sourceIds: rule.source_ids || (rule.source_id ? [rule.source_id] : []),
        sourceType: sourceTypeResolved,
        actionTypes: actionTypes.length > 0 ? actionTypes : [],
        actionTypesDisplay: actionTypes.join(", "),
        createdDate: rule.created_at
          ? new Date(rule.created_at).toISOString().split("T")[0]
          : new Date().toISOString().split("T")[0],
      };
    });
    setAlertRules(transformedRules);
  }, []);

  return (
    <AlertDataContext.Provider
      value={{
        alertRules,
        alerts,
        sourceData,
        cameras,
        addAlertRule,
        updateAlertRule,
        deleteAlertRule,
        toggleAlertRuleActive,
        generateAlertsForRule,
        clearAllAlerts,
        refreshAlerts,
        loadAlertRulesFromAPI,
      }}
    >
      {children}
    </AlertDataContext.Provider>
  );
}

/* --- HOOK --- */

export function useAlertData() {
  const context = useContext(AlertDataContext);
  if (!context) {
    throw new Error("useAlertData must be used within an AlertDataProvider");
  }
  return context;
}
